import { useEffect } from "react";

/**
 * Lightweight SEO hook — sets <title>, meta description and Open Graph tags
 * on the fly without pulling in react-helmet. Cleans up on unmount so the
 * previous route's tags don't leak.
 */
const SITE_NAME = "SRES · Shanvi Resource Estimation Studio";
const BASE_URL = "https://sres.preview.emergentagent.com";
const OG_IMAGE = `${BASE_URL}/sres-logo-horizontal.png?v=5`;

function upsertMeta(selector, attr, value) {
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement("meta");
    const [key, val] = selector.replace(/[\[\]"]/g, "").split("=");
    tag.setAttribute(key, val);
    document.head.appendChild(tag);
  }
  tag.setAttribute(attr, value);
  return tag;
}

export default function useSEO({ title, description, keywords, path = "" }) {
  useEffect(() => {
    // Full brand string for social/OG metadata …
    const socialTitle = title ? `${title} · SRES` : SITE_NAME;
    // …but the browser tab title must always read exactly "SRES" so the
    // window/tab identity stays consistent across the entire app, matching
    // sister products (SERP, SMIN).
    document.title = "SRES";

    const desc = description || "SRES is the Resource Estimation Studio for defensible mineral resource estimation, kriging block models and Competent Person Reports.";
    const url = `${BASE_URL}${path}`;

    upsertMeta('meta[name="description"]', "content", desc);
    if (keywords) upsertMeta('meta[name="keywords"]', "content", keywords);

    upsertMeta('meta[property="og:title"]', "content", socialTitle);
    upsertMeta('meta[property="og:description"]', "content", desc);
    upsertMeta('meta[property="og:type"]', "content", "website");
    upsertMeta('meta[property="og:url"]', "content", url);
    upsertMeta('meta[property="og:image"]', "content", OG_IMAGE);
    upsertMeta('meta[property="og:site_name"]', "content", SITE_NAME);

    upsertMeta('meta[name="twitter:card"]', "content", "summary_large_image");
    upsertMeta('meta[name="twitter:title"]', "content", socialTitle);
    upsertMeta('meta[name="twitter:description"]', "content", desc);
    upsertMeta('meta[name="twitter:image"]', "content", OG_IMAGE);

    // Canonical link
    let canon = document.head.querySelector('link[rel="canonical"]');
    if (!canon) {
      canon = document.createElement("link");
      canon.setAttribute("rel", "canonical");
      document.head.appendChild(canon);
    }
    canon.setAttribute("href", url);
  }, [title, description, keywords, path]);
}
